import { Landmark } from "lucide-react";
import { formatCurrency, formatPercent } from "~/lib/format";
import type { CompanyProfile } from "~/types/company";
import { OverviewCard } from "./card";

const RISK_CONFIG = {
	low: {
		label: "Nízká",
		bg: "bg-green-50",
		text: "text-green-700",
		border: "border-green-200",
		bar: "bg-green-500",
	},
	moderate: {
		label: "Střední",
		bg: "bg-yellow-50",
		text: "text-yellow-700",
		border: "border-yellow-200",
		bar: "bg-yellow-500",
	},
	high: {
		label: "Vysoká",
		bg: "bg-red-50",
		text: "text-red-700",
		border: "border-red-200",
		bar: "bg-red-500",
	},
} as const;

export function GovernmentDependencyCard({
	dependency,
}: {
	dependency: NonNullable<CompanyProfile["governmentDependency"]>;
}) {
	const risk = dependency.riskLevel ? RISK_CONFIG[dependency.riskLevel] : null;
	const yearSuffix = dependency.revenueYear ? ` (${dependency.revenueYear})` : "";

	return (
		<OverviewCard
			icon={Landmark}
			title="Závislost na smlouvách se státem"
			trailing={
				risk && (
					<span
						className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${risk.bg} ${risk.text} ${risk.border}`}
					>
						{risk.label} závislost
					</span>
				)
			}
		>
			<div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
				<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
					<p className="text-xs font-medium text-muted-foreground">Smlouvy{yearSuffix}</p>
					<p className="text-2xl font-mono font-semibold tabular-nums">
						{dependency.contractCount}
					</p>
				</div>
				{dependency.totalContractValueCzk != null && (
					<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
						<p className="text-xs font-medium text-muted-foreground">Hodnota smluv{yearSuffix}</p>
						<p className="text-2xl font-mono font-semibold tabular-nums">
							{formatCurrency(dependency.totalContractValueCzk)}
						</p>
					</div>
				)}
				{dependency.latestRevenue != null && (
					<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
						<p className="text-xs font-medium text-muted-foreground">Tržby{yearSuffix}</p>
						<p className="text-2xl font-mono font-semibold tabular-nums">
							{formatCurrency(dependency.latestRevenue)}
						</p>
					</div>
				)}
				{dependency.ratio != null && (
					<div className="flex flex-col gap-1 rounded-lg bg-gray-50 p-4">
						<p className="text-xs font-medium text-muted-foreground">
							Podíl na tržbách{yearSuffix}
						</p>
						<p className="text-2xl font-mono font-semibold tabular-nums">
							{formatPercent(dependency.ratio)}
						</p>
						{risk && (
							<div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
								<div
									className={`h-1.5 rounded-full ${risk.bar}`}
									style={{ width: `${Math.min(dependency.ratio * 100, 100)}%` }}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</OverviewCard>
	);
}
